/**
 * @file   src\components\GeneratePaxUniqueId.tsx
 * @brief  Generate Pax unique ID component.
 * @date   MAR, 2026
 * @author ZCO Engineer
 * @copyright (c) 2026, ZCO
 */

import { Button, useEffect, useState } from '../utils/thirdpartyComponents';
import CWInput from './CWInput';
import { MessageToaster } from '../utils/toastUtil';
import axios from '../config/axiosConfig';
import SmallLoader from './SmallLoader';
import { CompanyApi } from '../utils/apiUrls';

interface GeneratePaxUniqueIdProps {
    carWashId?: string | number;
    paxCarwashId?: string;
}

const GeneratePaxUniqueId = ({ carWashId, paxCarwashId: initialPaxCarwashId }: GeneratePaxUniqueIdProps) => {
    const [paxUniqueId, setPaxUniqueId] = useState('');
    const [loading, setLoading] = useState(false);
    const toaster = new MessageToaster();

    useEffect(() => {
        if (initialPaxCarwashId) {
            setPaxUniqueId(initialPaxCarwashId);
        }
    }, [initialPaxCarwashId]);

    const buildPaxUniqueId = () => {
        const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase();
        const timePart = Date.now().toString(36).toUpperCase();
        const idPart = carWashId ? String(carWashId).toUpperCase() : 'NA';
        return `PAX-${timePart}-${randomPart}`;
    };

    const generatePaxUniqueId = async () => {
        if (!carWashId) {
            toaster.toastError('Car wash ID is required');
            return;
        }
        try {
            setLoading(true);
            const paxCarwashId = buildPaxUniqueId();
            const requestParam = {
                carWashId: parseInt(String(carWashId), 10),
                paxCarwashId,
            };
            const { data } = await axios.post(CompanyApi.UPDATE_PAX_CARWASH_ID, requestParam);
            if (data?.error === 0) {
                setPaxUniqueId(data?.data?.paxCarwashId ?? paxCarwashId);
                toaster.toastSuccess(data?.message || 'Pax ID generated successfully');
            } else {
                toaster.toastError(data?.message || 'Failed to generate Pax ID');
            }
        } catch (error: any) {
            toaster.toastError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="app-field-group d-flex align-items-center">
            <div className='copy-input d-flex align-items-center'>
                <CWInput
                    id="paxUniqueId"
                    name="paxUniqueId"
                    type="text"
                    label="Pax Unique ID"
                    value={paxUniqueId}
                    disabled
                />
            </div>
            <Button
                title="Generate Pax ID"
                type="button"
                variant="primary"
                className="primary-btn"
                onClick={generatePaxUniqueId}
                disabled={!!paxUniqueId || loading}
            >
                Generate Pax ID
            </Button>
            {loading && <SmallLoader />}
        </div>
    );
};

export default GeneratePaxUniqueId;
